import { ApiClient, type TokenPair, type TokenStore } from '@fsm/shared';

const STORAGE_KEY = 'fsm.tokens.v1';

/**
 * Web token store: memory for speed, localStorage so a refresh does not sign
 * you out. The mobile app swaps this for expo-secure-store and shares
 * everything else.
 *
 * A refresh token in localStorage is readable by any script that gets onto the
 * page. That is the accepted trade for a same-origin office tool with short
 * access tokens and revocable, rotating refresh tokens; a public-facing
 * deployment should move this to an httpOnly cookie on the web side while the
 * phone keeps bearer tokens.
 */
function browserTokenStore(): TokenStore {
  let cached: TokenPair | null | undefined;

  return {
    async get() {
      if (cached !== undefined) return cached;
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        cached = raw ? (JSON.parse(raw) as TokenPair) : null;
      } catch {
        cached = null;
      }
      return cached;
    },
    async set(tokens) {
      cached = tokens;
      try {
        if (tokens) localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
        else localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Private browsing with storage disabled: the session still works, it
        // just will not survive a reload.
      }
    },
  };
}

let onUnauthenticated: () => void = () => {};

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

export const api = new ApiClient({
  // Vite proxies /api to the API in dev; in production this is the same origin
  // or an env-provided absolute URL.
  baseUrl: import.meta.env['VITE_API_URL'] ?? '/api',
  tokens: browserTokenStore(),
  onUnauthenticated: () => onUnauthenticated(),
});

export { ApiError } from '@fsm/shared';
